import { graphql } from 'react-relay';
import { useEffect } from 'react';

import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useShareableLinkPermissionsForObjectShareableLinkPermissionsForObjectQuery$data,
	useShareableLinkPermissionsForObjectShareableLinkPermissionsForObjectQuery$variables,
} from './__generated__/useShareableLinkPermissionsForObjectShareableLinkPermissionsForObjectQuery.graphql.ts';

/**
 * Object type for permission queries. Mirrors the GraphQL ShareableLinkPermissionsObjectType enum.
 */
enum ShareableLinkPermissionsObjectType {
	CONTENT = 'CONTENT',
}

/**
 * Permission type for shareable links. Mirrors the GraphQL ShareableLinkPermissionsType enum.
 */
enum ShareableLinkPermissionsType {
	EDIT = 'EDIT',
}

export type ShareableLinkPermissionsForObjectData = {
	hasEditPermission: boolean;
	permissions: readonly string[];
};

export type ShareableLinkPermissionsForObjectQueryData = {
	isLoading: boolean;
	refetch: () => void;
	error?: Error;
	data?: ShareableLinkPermissionsForObjectData;
};

/**
 * Fetches edit permissions for a shareable link object (e.g., whiteboard).
 *
 * Queries whether the shareable link has edit permissions for the given object,
 * which determines if editable public whiteboards are supported.
 */
export const useShareableLinkPermissionsForObject = ({
	objectId,
	objectType = ShareableLinkPermissionsObjectType.CONTENT,
	skip,
}: {
	objectId: string;
	objectType?: ShareableLinkPermissionsObjectType;
	skip?: boolean;
}): ShareableLinkPermissionsForObjectQueryData => {
	const { loading, error, data, refetch } = useQueryInterop<
		useShareableLinkPermissionsForObjectShareableLinkPermissionsForObjectQuery$data,
		useShareableLinkPermissionsForObjectShareableLinkPermissionsForObjectQuery$variables
	>(
		graphql`
			query useShareableLinkPermissionsForObjectShareableLinkPermissionsForObjectQuery(
				$objectId: ID!
				$objectType: PublicLinkPermissionsObjectType!
			) {
				publicLinkPermissionsForObject(objectId: $objectId, objectType: $objectType) {
					permissions
				}
			}
		`,
		{
			variables: {
				objectId,
				objectType,
			},
			skip,
		},
	);

	useEffect(() => {
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
		}
	}, [error]);

	const hasEditPermission: boolean =
		data?.publicLinkPermissionsForObject?.permissions?.includes(
			ShareableLinkPermissionsType.EDIT,
		) ?? false;

	if (loading) return { isLoading: true, refetch };

	return {
		isLoading: false,
		error,
		refetch,
		data: error
			? undefined
			: {
					hasEditPermission,
					permissions: data?.publicLinkPermissionsForObject?.permissions ?? [],
				},
	};
};
